import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePlaylist } from "../../../contexts/PlaylistContext";
import { keyBelongsToControl } from "../../../utils/shortcutGuard";
import { getOptimizedImageUrl } from "../../_helpers";

interface MusicPlayerModalProps {
  isVisible: boolean;
  onClose: () => void;
}

export default function MusicPlayerModal({ isVisible, onClose }: MusicPlayerModalProps) {
  const { currentTrack, isPlaying, togglePlayPause, audioRef } = usePlaylist();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!audioRef?.current) return;

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [audioRef]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (keyBelongsToControl(e)) return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        togglePlayPause();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose, togglePlayPause]);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef?.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressWidth = rect.width;
    const seekTime = (clickX / progressWidth) * duration;
    audioRef.current.currentTime = seekTime;
  };

  const artwork = currentTrack?.artwork || (currentTrack as any)?.album?.images?.[0]?.url;
  const trackName = currentTrack?.name || currentTrack?.title || "No track";
  const artistName = currentTrack?.artist || (currentTrack as any)?.artists?.[0]?.name || "";

  if (typeof window === "undefined") return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              zIndex: 9998,
            }}
          />
          <motion.div
            role="dialog"
            aria-label="Now playing"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 250,
              mass: 1,
            }}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                backgroundColor: "var(--color5)",
                borderTopLeftRadius: "1.5rem",
                borderTopRightRadius: "1.5rem",
                padding: { xs: "2rem", md: "2rem 3rem" },
                boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: { xs: "1.5rem", md: "1rem" },
                }}
              >
                <Typography
                  sx={{
                    color: "var(--logoColor2)",
                    fontSize: { xs: "2rem", md: "1.25rem" },
                    fontFamily: "Hanson",
                    fontWeight: "700",
                  }}
                >
                  NOW PLAYING
                </Typography>
                <IconButton
                  onClick={onClose}
                  aria-label="Close music player"
                  sx={{
                    color: "var(--logoColor)",
                    padding: { xs: "1rem", md: "0.5rem" },
                    "&:hover": {
                      color: "var(--logoColor2)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: "2.5rem", md: "1.5rem" } }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "2rem", md: "1.5rem" },
                  mb: { xs: "2rem", md: "1.5rem" },
                }}
              >
                {artwork && (
                  <Box
                    component="img"
                    src={getOptimizedImageUrl(artwork, { width: 160, height: 160, fit: "cover" })}
                    alt={trackName}
                    sx={{
                      width: { xs: "8rem", md: "5rem" },
                      height: { xs: "8rem", md: "5rem" },
                      borderRadius: "0.5rem",
                      objectFit: "cover",
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "var(--logoColor2)",
                      fontSize: { xs: "2rem", md: "1.25rem" },
                      fontFamily: "Poppins",
                      fontWeight: "600",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {trackName}
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--logoColor)",
                      fontSize: { xs: "1.5rem", md: "1rem" },
                      fontFamily: "Poppins",
                      fontWeight: "400",
                      opacity: 0.8,
                    }}
                  >
                    {artistName}
                  </Typography>
                </Box>
                <IconButton
                  onClick={togglePlayPause}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  sx={{
                    backgroundColor: "var(--logoColor2)",
                    color: "var(--color5)",
                    padding: { xs: "1.5rem", md: "1rem" },
                    "&:hover": {
                      backgroundColor: "var(--logoColor)",
                    },
                  }}
                >
                  {isPlaying ? (
                    <PauseIcon sx={{ fontSize: { xs: "3rem", md: "2rem" } }} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: { xs: "3rem", md: "2rem" } }} />
                  )}
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "1rem", md: "0.75rem" } }}>
                <Typography
                  sx={{
                    color: "var(--logoColor)",
                    fontSize: { xs: "1.25rem", md: "0.875rem" },
                    fontFamily: "Poppins",
                    minWidth: { xs: "4rem", md: "3rem" },
                  }}
                >
                  {formatTime(currentTime)}
                </Typography>
                <Box
                  ref={progressRef}
                  onClick={handleSeek}
                  sx={{
                    flex: 1,
                    height: { xs: "0.75rem", md: "0.5rem" },
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "1rem",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: duration ? `${Math.min(100, (currentTime / duration) * 100)}%` : "0%",
                      backgroundColor: "var(--logoColor2)",
                      borderRadius: "1rem",
                      transition: "width 0.1s linear",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: "var(--logoColor)",
                    fontSize: { xs: "1.25rem", md: "0.875rem" },
                    fontFamily: "Poppins",
                    minWidth: { xs: "4rem", md: "3rem" },
                    textAlign: "right",
                  }}
                >
                  {formatTime(duration)}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
